<template>
    <div 
        class="guide" 
        :style="cssVariablesGuide" 
        @focusin="onFocusIn"
        @focusout="onFocusOut"
    >
        <div class="guide-timeline">
            <guide-focused-day />
            <guide-timeline />
        </div>
        <div ref="grid" class="guide-grid">
            <div>
                <div id="beforeRows" :key="`beforeRows_${observerRowsKey}`" ref="beforeRows" class="guide-observer-line" />
                <div
                    v-for="({channel, items}, channelIndex) of guideGrid"
                    :key="`${channel.id}`"
                    class="guide-grid-row"
                    :data-channelid="channel.id"
                >
                    <guide-channel :channel="channel" :channel-idx="channelIndex" @loaded-row="loadedRow" />
                    <guide-program-row 
                        :items="items" 
                        :channel-idx="channelIndex"
                        :ref-grid="refGrid"
                        :channel-id="channel.id"
                        @click-program="onClickProgram" 
                    />
                </div>
                <div id="afterRows" :key="`afterRows_${observerRowsKey}`" ref="afterRows" class="guide-observer-line" />
            </div>
        </div>
        <empty-data v-if="isEmptyGuide" />
    </div>
</template>

<script setup lang="ts">
    import { type scrollGuideEvent, scrollTarget } from "@/components/Guide/types";
    import { ref, computed, type Ref, watch, onMounted, nextTick, onBeforeUnmount } from "vue";
    import type { ExposeInterface} from "./types";
    import { useGuideStore } from "@/stores/Guide";
    import { storeToRefs } from 'pinia';

    const GuideStoreModule = useGuideStore();
    const {
        updatePrograms,
    } = GuideStoreModule;
    const {
        channelsViewPortIsEmpty: isEmptyGuide,
        viewportStartTime,
        channelWidth: channelWidthNumber,
        gapGuideGrid: gapGuide,
        channelsViewPort: guideGrid,
    } = storeToRefs(GuideStoreModule)


    const props = defineProps({
        observerRowsKey: {
            type: Number,
            required: true
        }
    });

    const emit = defineEmits([
        'on-scroll-guide', 
        'on-focus-in',
        'on-focus-out',
        'on-update-height-guide',
        'click-program',
        'load-prev-row',
        'load-next-row',
        'is-ready-guide'
    ]); 

    const { guideConfig } = useAppConfig();
    const featureScrollbarEnabled = guideConfig.featureScrollbarEnabled;
    const observersEnabled = !guideConfig.featureUpdateViewportChannelsEnabled;
    const heightGuide: Ref<number> = ref(0);
    const counterRow: Ref<number> = ref(1);
    const observer: Ref<IntersectionObserver | null> = ref(null);
    const isStartedObserver: Ref<boolean>= ref(false);
    const grid: Ref<HTMLElement | null> = ref(null);
    const refGuideGrid: Ref<HTMLElement | null> = ref(null);
    const offsetSaved: Ref<number> = ref(0);
    const beforeRows: Ref<HTMLElement | null> = ref(null);
    const afterRows: Ref<HTMLElement | null> = ref(null);
        

    const refGrid = computed((): HTMLElement | null  => refGuideGrid.value ?? grid.value);
    const cssVariablesGuide = computed((): {[key: string]: string} => {
        return {'--width-channel': channelWidth.value, '--height-guide': `${heightGuide.value}px`, '--gap-guide': `${gapGuide.value}px`}
    });
    const channelWidth = computed((): string => {
        return `${Math.floor(channelWidthNumber.value)}px`;
    });
    const isLoadingRows = computed((): boolean  => guideGrid.value !== null && counterRow.value === guideGrid.value.length);


    const handle = () => {
        getHeightGrid();
    };
    const scrollToGuide = (offset: number): void => {
        if (refGrid.value) {
            refGrid.value.scrollTo({
                top: (refGrid.value.scrollHeight * offset) / 100,
                behavior: 'smooth'
            });
        }
    };
    const initDefaultScrollGuide = (): void => {
        setOffsetScrollbar(0);
    };
    const setOffsetScrollbar = (offsetInPercent: number) => {
        if (!refGrid.value) {
            return;
        }
        let ratioHeights = Number((refGrid.value.scrollHeight / heightGuide.value));
        ratioHeights = ratioHeights < 1 ? 1 : ratioHeights;
        ratioHeights = ratioHeights > 7 ? 7 : ratioHeights;

        const scrollEvent: scrollGuideEvent = {
            offset: offsetInPercent,
            ratioHeightThumb: ratioHeights,
            target: scrollTarget.GUIDE
        };
        emit('on-scroll-guide', scrollEvent);
    };
    const onScrollGrid = (event: Event): void => {
        const target = event.target as HTMLElement;
        let offsetInPercent: number = Number((target.scrollTop  * 100 / target.scrollHeight));
        offsetInPercent = offsetInPercent >= 98 ? 100 : offsetInPercent;

        if (offsetSaved.value === offsetInPercent){
            return;
        }
        offsetSaved.value = offsetInPercent;
        
        setOffsetScrollbar(offsetInPercent);
    };
    const getRefGrid = (): void => {
        refGuideGrid.value = grid.value;
    };
    const onFocusIn = (): void => {
        emit('on-focus-in');
    };
    const onFocusOut = (): void => {
        emit('on-focus-out');
    };
    const onClickProgram = (): void => {
        emit('click-program');
    };
    const loadedRow = ()=> {
        if (guideGrid.value !== null && counterRow.value !== guideGrid.value.length){
            counterRow.value++;
        }
    };
    const createIntersectionObserver = (): void => {
        const marginTopBottom = `${heightGuide.value / 2}px`;
        
        if (grid.value) {
            const options = {
                root: grid.value,
                rootMargin: `${marginTopBottom} 0px ${marginTopBottom} 0px`
            };
            observer.value = new IntersectionObserver(callbacksIntersectionObserver, options);
        }
    };
    const callbacksIntersectionObserver = (entries: IntersectionObserverEntry[]): void => {
        entries.forEach((entry) => {
            if (entry.target.id === 'beforeRows' && entry.isIntersecting && isStartedObserver.value) {
                emit('load-prev-row');
            }

            if (entry.target.id === 'afterRows' && entry.isIntersecting  && isStartedObserver.value) {
                emit('load-next-row');
            }
        });
    };
    const startIntersectionObserver = (): void => {
        if (observer.value == null) {
            return;
        }

        if (beforeRows.value) {
            observer.value.observe(beforeRows.value);
        }
        if (afterRows.value) {
            observer.value.observe(afterRows.value);
        }
    };
    const disconnectIntersectionObserver = (): void => {
        if (observer.value == null) {
            return;
        }
        observer.value.disconnect();

    };
    const getHeightGrid = () => {
        if (grid.value) {
            const { y } = grid.value.getBoundingClientRect();
            const { top} = grid.value.getBoundingClientRect();
            const gridHeight = y ?? top;
            const height = window.innerHeight - gridHeight;
            
            heightGuide.value = height;
        }
    };
    const regScrollGuideEvent = (): void => {
        refGrid.value?.addEventListener('scroll', onScrollGrid);
    };
    const unRegScrollGuideEvent = (): void => {
        refGrid.value?.removeEventListener('scroll', onScrollGrid);
    };

    watch(
        heightGuide,
        () => {
            emit('on-update-height-guide', heightGuide.value);
        }
    );
    watch(
        () => props.observerRowsKey,
        () => {
            counterRow.value = 1;
            isStartedObserver.value = false;
        }
    );
    watch(
        isLoadingRows,
        (newValue: boolean) => {
            if (newValue && !isStartedObserver.value){
                startIntersectionObserver();
                isStartedObserver.value = true;
                emit('is-ready-guide', newValue);

                
                if (featureScrollbarEnabled) {
                    initDefaultScrollGuide();
                }
            }
        }
    );
    watch(
        viewportStartTime,
        () => {
            updatePrograms();
        }
    );

    onMounted(() => {
        nextTick(() => {
            getHeightGrid();

            if(observersEnabled) {
                createIntersectionObserver();
            }
            
            window.addEventListener('resize', handle);
            getRefGrid();

            if (featureScrollbarEnabled) {
                regScrollGuideEvent();
            }
        });
    });

    onBeforeUnmount(() => {
        if(observersEnabled) {
            disconnectIntersectionObserver();
        }

        if (featureScrollbarEnabled) {
            unRegScrollGuideEvent();
        }
    });

    defineExpose<ExposeInterface>({
        scrollToGuide
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>